/**
 * React hooks for accessing the derived entity registry.
 *
 * The registry is recomputed only when one of its input slices changes
 * by reference. Camera moves, UI state, gizmo drags — none of these
 * trigger a rebuild.
 */

import { useMemo } from "react";
import { useStore } from "../app/store.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import { buildRegistry, type RegistryInput } from "./buildRegistry.ts";
import type { ComponentType, ComponentTypeMap } from "./components.ts";
import type { Entity, EntityRegistry } from "./Entity.ts";
import type { EntityId } from "./EntityId.ts";
import { entityIdToStoreKey } from "./EntityId.ts";

// ---------------------------------------------------------------------------
// Slice selectors — one per input field, so Zustand only re-renders
// when the specific reference changes.
// ---------------------------------------------------------------------------

const selectBlendFiles = (s: { blendFiles: unknown }) => s.blendFiles;
const selectSceneObjects = (s: { sceneObjects: unknown }) => s.sceneObjects;
const selectCollections = (s: { collections: unknown }) => s.collections;
const selectVisibility = (s: { visibility: unknown }) => s.visibility;
const selectModifierOverrides = (s: { modifierOverrides: unknown }) =>
  s.modifierOverrides;
const selectShapeKeyWeights = (s: { shapeKeyWeights: unknown }) =>
  s.shapeKeyWeights;
const selectMannequinMorphs = (s: { mannequinMorphs: unknown }) =>
  s.mannequinMorphs;
const selectMannequinBonePoses = (s: { mannequinBonePoses: unknown }) =>
  s.mannequinBonePoses;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Returns the derived entity registry. Recomputed only when relevant
 * store slices change by reference.
 */
export function useEntityRegistry(): EntityRegistry {
  // Each selector subscribes independently — Zustand v5 single-arg selectors
  const blendFiles = useStore(selectBlendFiles);
  const sceneObjects = useStore(selectSceneObjects);
  const collections = useStore(selectCollections);
  const materials = useStore((s) => s.materials);
  const visibility = useStore(selectVisibility);
  const modifierOverrides = useStore(selectModifierOverrides);
  const shapeKeyWeights = useStore(selectShapeKeyWeights);
  const mannequinMorphs = useStore(selectMannequinMorphs);
  const mannequinBonePoses = useStore(selectMannequinBonePoses);

  const registry = useMemo(
    () =>
      buildRegistry({
        blendFiles,
        sceneObjects,
        collections,
        materials,
        visibility,
        modifierOverrides,
        shapeKeyWeights,
        mannequinMorphs,
        mannequinBonePoses,
      } as RegistryInput),
    [
      blendFiles,
      sceneObjects,
      collections,
      materials,
      visibility,
      modifierOverrides,
      shapeKeyWeights,
      mannequinMorphs,
      mannequinBonePoses,
    ],
  );

  return registry;
}

/** Returns a single entity by id, or undefined if not found. */
export function useEntity(id: EntityId | null | undefined): Entity | undefined {
  const registry = useEntityRegistry();
  return id ? registry.get(id) : undefined;
}

/**
 * Returns a specific component for an entity.
 * The return type is narrowed by the component type literal.
 *
 * ```ts
 * const t = useComponent(eid, "transform"); // TransformComponent | undefined
 * ```
 */
export function useComponent<K extends ComponentType>(
  id: EntityId | null | undefined,
  type: K,
): ComponentTypeMap[K] | undefined {
  const entity = useEntity(id);
  return entity?.components[type] as ComponentTypeMap[K] | undefined;
}

/**
 * Returns true if the entity is in the current selection.
 */
export function useIsSelected(id: EntityId | null | undefined): boolean {
  const selectedObjects = useSelectedLeafObjectIds();
  if (!id) return false;
  return selectedObjects.has(entityIdToStoreKey(id));
}
